import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Send, Trash2, CheckCheck, Loader2, Plus } from 'lucide-react';
import { API, errText } from '../../api';
import FileDrop from '../FileDrop';
import RichText from '../RichText';
import { PostMedia } from '../dashboard/ApprovalInbox';

const PLATFORMS = ['Instagram', 'Facebook', 'TikTok', 'YouTube', 'X', 'LinkedIn', 'Google Business'];
const STATUS = { pending: ['Awaiting approval', 'text-gold border-gold/40'], approved: ['Approved — post it', 'text-emerald-400 border-emerald-500/40'], changes: ['Changes requested', 'text-heart border-heart/40'], posted: ['Posted', 'text-cream/45 border-cream/20'], draft: ['Draft', 'text-cream/45 border-cream/20'] };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Date TBD');
const EMPTY = { owner_email: '', platform: 'Instagram', caption: '', media_url: null, media_type: null, scheduled_for: '' };

function Composer({ customers, onCreated }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.owner_email || !f.caption.trim()) { toast.error('Pick a customer and write the caption.'); return; }
    setBusy(true);
    try {
      await API.createPost({ ...f, scheduled_for: f.scheduled_for ? new Date(f.scheduled_for).toISOString() : null });
      toast.success('Sent for approval.', { description: 'The customer gets an email and sees it in their dashboard.' });
      setF(EMPTY); onCreated();
    } catch (e) { toast.error(errText(e, 'Could not send.')); }
    finally { setBusy(false); }
  };
  return (
    <div className="panel rounded-3xl p-6 space-y-4" data-testid="post-composer">
      <p className="font-display text-2xl text-cream flex items-center gap-2"><Plus size={18} className="text-gold" /> New post for approval</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <select value={f.owner_email} onChange={(e) => set('owner_email', e.target.value)} data-testid="post-customer" className="rounded-xl border hairline bg-panel2/50 px-4 py-3 text-sm text-cream outline-none focus:border-gold/50">
          <option value="">Customer…</option>
          {customers.map((c) => <option key={c.email} value={c.email}>{c.business_name || c.email}</option>)}
        </select>
        <select value={f.platform} onChange={(e) => set('platform', e.target.value)} data-testid="post-platform" className="rounded-xl border hairline bg-panel2/50 px-4 py-3 text-sm text-cream outline-none focus:border-gold/50">
          {PLATFORMS.map((p) => <option key={p}>{p}</option>)}
        </select>
        <input type="datetime-local" value={f.scheduled_for} onChange={(e) => set('scheduled_for', e.target.value)} data-testid="post-date" className="rounded-xl border hairline bg-panel2/50 px-4 py-3 text-sm text-cream outline-none focus:border-gold/50" />
      </div>
      <textarea value={f.caption} onChange={(e) => set('caption', e.target.value)} rows={5} placeholder="Caption, hashtags, link…" data-testid="post-caption" className="w-full rounded-xl border hairline bg-panel2/50 px-4 py-3 text-sm text-cream outline-none focus:border-gold/50 resize-y" />
      <div className="flex items-center gap-4 flex-wrap">
        <FileDrop accept="video/*,image/*" onUploaded={(res, file) => { set('media_url', res.url); set('media_type', file.type); }} testId="post-media-upload" className="flex-1">
          <span className="text-sm">{f.media_url ? 'Media attached — drop another to replace' : 'Drop the video or image for this post'}</span>
        </FileDrop>
        <button onClick={submit} disabled={busy} data-testid="post-send-btn" className="btn-gold rounded-full px-6 py-3 text-sm flex items-center gap-2 disabled:opacity-50">{busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send for approval</button>
      </div>
    </div>
  );
}

export default function ContentQueue() {
  const [posts, setPosts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const load = () => API.posts().then(setPosts).catch(() => {});
  useEffect(() => { load(); API.customers().then((c) => setCustomers(c.customers || c)).catch(() => {}); }, []);
  const mark = async (p, status) => { try { await API.patchPost(p.id, { status }); load(); } catch (e) { toast.error('Could not update.'); } };
  const del = async (p) => { if (!window.confirm('Delete this post?')) return; try { await API.deletePost(p.id); load(); } catch (e) { toast.error('Could not delete.'); } };
  return (
    <div className="space-y-8" data-testid="studio-content">
      <Composer customers={customers} onCreated={load} />
      <div className="space-y-3">
        <p className="eyebrow text-[9px]">Queue &middot; {posts.length}</p>
        {!posts.length && <p className="text-sm text-cream/50 rounded-2xl border hairline p-6">Nothing queued. Posts you send appear here with the customer&rsquo;s decision.</p>}
        {posts.map((p) => (
          <div key={p.id} className="rounded-2xl border hairline p-4" data-testid={`queue-post-${p.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-cream">{p.business} &middot; {p.platform} &middot; <span className="text-cream/55">{fmt(p.scheduled_for)}</span></p>
              <div className="flex items-center gap-3">
                <span className={`text-[10px] tracking-widest uppercase border rounded-full px-2 py-0.5 ${STATUS[p.status]?.[1]}`} data-testid={`queue-status-${p.id}`}>{STATUS[p.status]?.[0] || p.status}</span>
                {p.status === 'approved' && <button onClick={() => mark(p, 'posted')} data-testid={`queue-posted-${p.id}`} className="text-xs text-emerald-400 hover:underline flex items-center gap-1"><CheckCheck size={13} /> Mark posted</button>}
                {p.status === 'changes' && <button onClick={() => mark(p, 'pending')} data-testid={`queue-resend-${p.id}`} className="text-xs text-gold hover:underline flex items-center gap-1"><Send size={13} /> Re-send</button>}
                <button onClick={() => del(p)} data-testid={`queue-delete-${p.id}`} className="text-cream/35 hover:text-heart"><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="mt-3 grid sm:grid-cols-[140px_1fr] gap-4">
              <PostMedia post={p} className="w-full max-h-40" />
              <div className="text-sm text-cream/75"><RichText text={p.caption} />{p.feedback && <p className="mt-2 text-xs text-heart">Customer: {p.feedback}</p>}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
